import { useState, useRef, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { useCategoryTree, type Category } from "@/hooks/useCategoryTree";
import { Skeleton } from "@/components/ui/skeleton";

const MegaMenuDropdown = ({ category, onClose }: { category: Category; onClose: () => void }) => {
  // Level 2 children are section headings, Level 3 are clickable links
  const level2Children = (category.children || []).filter(
    (c) => c.is_active && c.is_published
  );

  const columnCount = Math.min(level2Children.length || 1, 4);

  return (
    <div
      className="absolute left-0 right-0 top-full bg-popover border-b border-border shadow-lg z-40"
      onMouseLeave={onClose}
    >
      <div className="container mx-auto px-4 py-6">
        {level2Children.length > 0 ? (
          <ul
            className="grid gap-8 list-none m-0 p-0"
            style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
            role="menu"
          >
            {level2Children.map((l2) => {
              const l3Children = (l2.children || []).filter(
                (c) => c.is_active && c.is_published
              );
              return (
                <li key={l2.id} role="none">
                  <span className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                    {l2.name}
                  </span>
                  <ul className="space-y-2 list-none m-0 p-0" role="menu">
                    {l3Children.map((l3) => (
                      <li key={l3.id} role="none">
                        <Link
                          to={`/${category.slug}/${l2.slug}/${l3.slug}`}
                          onClick={onClose}
                          role="menuitem"
                          className="text-sm text-foreground hover:text-primary transition-colors font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded px-1 -mx-1"
                        >
                          {l3.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        ) : null}
        <div className="mt-5 pt-4 border-t border-border/50">
          <Link
            to={`/${category.slug}`}
            onClick={onClose}
            className="text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            View All {category.name} →
          </Link>
        </div>
      </div>
    </div>
  );
};

const LABEL_OVERRIDES: Record<string, string> = {
  "Modest Activewear": "Activewear",
  "Prayer Wear": "Prayer",
};

const MainNav = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { publishedTree, isLoading } = useCategoryTree();

  // Get Level 1 categories (children of root nodes that are published)
  const level1Categories = publishedTree.flatMap((root) =>
    (root.children || []).filter((c) => c.is_active && c.is_published)
  );

  const handleEnter = useCallback((index: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpenIndex(index), 150);
  }, []);

  const handleLeave = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpenIndex(null), 100);
  }, []);

  const handleDropdownEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === "Escape") {
      setOpenIndex(null);
    } else if (e.key === "Enter" || e.key === " ") {
      setOpenIndex(openIndex === index ? null : index);
    }
  }, [openIndex]);

  return (
    <nav aria-label="Main navigation" className="hidden md:block fabric-texture relative">
      <div className="container mx-auto px-4">
        {isLoading ? (
          <div className="flex items-center justify-center gap-4 py-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-5 w-20" />
            ))}
          </div>
        ) : (
          <ul className="flex items-center justify-center gap-1 list-none m-0 p-0" role="menubar">
            {level1Categories.map((cat, i) => {
              const hasChildren = (cat.children || []).some(
                (c) => c.is_active && c.is_published
              );

              return (
                <Fragment key={cat.id}>
                  <li
                    className="relative"
                    role="none"
                    onMouseEnter={() => handleEnter(i)}
                    onMouseLeave={handleLeave}
                  >
                    <Link
                      to={`/${cat.slug}`}
                      role="menuitem"
                      aria-haspopup={hasChildren ? "true" : undefined}
                      aria-expanded={hasChildren ? openIndex === i : undefined}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      className="block px-3 lg:px-4 py-3 text-sm font-semibold tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded text-primary hover:opacity-80 whitespace-nowrap"
                      style={cat.font_color ? { color: cat.font_color } : undefined}
                    >
                      {LABEL_OVERRIDES[cat.name] || cat.name}
                      {cat.badge_label && (
                        <span
                          className="ml-1.5 inline-flex items-center justify-center text-[9px] font-bold uppercase leading-none px-1.5 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: cat.badge_color || "hsl(var(--primary))" }}
                        >
                          {cat.badge_label}
                        </span>
                      )}
                    </Link>
                  </li>
                </Fragment>
              );
            })}
          </ul>
        )}
      </div>

      {openIndex !== null && level1Categories[openIndex] && (
        <div onMouseEnter={handleDropdownEnter} onMouseLeave={handleLeave}>
          <MegaMenuDropdown
            category={level1Categories[openIndex]}
            onClose={() => setOpenIndex(null)}
          />
        </div>
      )}
    </nav>
  );
};

export default MainNav;
